import { useState, type CSSProperties, type ReactNode } from 'react';
import {
  AppstoreOutlined,
  CheckOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  SettingOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Button,
  Descriptions,
  Empty,
  Input,
  Popover,
  Segmented,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  theme,
} from 'antd';
import { useTranslation } from 'react-i18next';
import type { Box, Position } from '../../pages/StoragePage';

type Action = 'putaway' | 'request' | 'remove';
type Display = 'none' | 'sampleNo' | 'cellLineMaterial' | 'label' | 'passage';
type Props = {
  box: Box;
  selected?: Position | undefined;
  selectedIds: string[];
  onSelectedIds: (ids: string[]) => void;
  onSelect: (position: Position) => void;
  view: 'grid' | 'list';
  onView: (view: 'grid' | 'list') => void;
  labelMode: 'alphanumeric' | 'numeric';
  onLabelMode: (mode: 'alphanumeric' | 'numeric') => void;
  display: Display;
  onDisplay: (display: Display) => void;
  positionLabel: (position: Position) => string;
  positionText: (position: Position) => string;
  sampleLabel: (
    sample: NonNullable<Position['sample']> | undefined,
  ) => string | undefined;
  ownerName: (
    sample: NonNullable<Position['sample']> | undefined,
  ) => string | undefined;
  sampleNotes: (
    sample: NonNullable<Position['sample']> | undefined,
  ) => string | undefined;
  sampleStatus: (status: string) => ReactNode;
  date: (value?: string | null) => string;
  canCreate: boolean;
  canRequest: boolean;
  canRemove: boolean;
  canEdit: boolean;
  onCreate: (positions: Position[]) => void;
  onEdit: (position: Position) => void;
  onRemove: (position: Position) => void;
  onRequest: (positions: Position[]) => void;
  onBatchRemove: () => void;
};

export function StorageBoxWorkspace(props: Props) {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const [action, setAction] = useState<Action | null>(null);
  const [search, setSearch] = useState('');
  const selected = props.box.positions.find(
    (position) => position.id === props.selected?.id,
  );
  const eligible = (position: Position) =>
    action === 'putaway'
      ? !position.occupied
      : position.occupied && Boolean(position.sample);
  const selectedRows = props.box.positions.filter(
    (position) => props.selectedIds.includes(position.id) && eligible(position),
  );
  const options = [
    {
      value: 'putaway' as const,
      label: t('storage.workspace.putaway'),
      disabled: !props.canCreate,
    },
    {
      value: 'request' as const,
      label: t('storage.createBatchRequest'),
      disabled: !props.canRequest,
    },
    {
      value: 'remove' as const,
      label: t('storage.removeSample'),
      disabled: !props.canRemove,
    },
  ];
  const start = () => {
    const next = options.find((option) => !option.disabled);
    if (next) {
      setAction(next.value);
      props.onSelectedIds([]);
    }
  };
  const select = (position: Position) => {
    if (!action) {
      props.onSelect(position);
      return;
    }
    if (!eligible(position)) return;
    props.onSelectedIds(
      props.selectedIds.includes(position.id)
        ? props.selectedIds.filter((id) => id !== position.id)
        : [...props.selectedIds, position.id],
    );
  };
  const toggleRow = (rowIndex: number) => {
    const ids = props.box.positions
      .filter((p) => p.rowIndex === rowIndex && eligible(p))
      .map((p) => p.id);
    props.onSelectedIds(
      ids.every((id) => props.selectedIds.includes(id))
        ? props.selectedIds.filter((id) => !ids.includes(id))
        : [...new Set([...props.selectedIds, ...ids])],
    );
  };
  const rows = [...new Set(props.box.positions.map((p) => p.rowIndex))].sort(
    (a, b) => a - b,
  );
  const columns = [
    ...new Set(props.box.positions.map((p) => p.columnIndex)),
  ].sort((a, b) => a - b);
  const keyword = search.trim().toLocaleLowerCase();
  const filtered = props.box.positions.filter(
    (position) =>
      !keyword ||
      [
        props.positionLabel(position),
        position.sample?.sampleNo,
        position.sample?.barcode,
        position.sample?.cellLineCode,
        position.sample?.materialName,
        props.sampleLabel(position.sample ?? undefined),
      ].some((value) => value?.toLocaleLowerCase().includes(keyword)),
  );
  const styles = {
    '--storage-border': token.colorBorderSecondary,
    '--storage-surface': token.colorBgContainer,
    '--storage-muted-bg': token.colorFillAlter,
    '--storage-primary': token.colorPrimary,
    '--storage-soft': token.colorPrimaryBg,
    '--storage-text': token.colorText,
    '--storage-muted': token.colorTextSecondary,
    '--storage-radius': `${token.borderRadius}px`,
  } as CSSProperties;
  return (
    <section className="storage-box-workbench" style={styles}>
      <div className="storage-workbench-toolbar">
        <Segmented
          value={props.view}
          onChange={props.onView}
          options={[
            {
              value: 'grid',
              icon: <AppstoreOutlined />,
              label: t('storage.gridView'),
            },
            {
              value: 'list',
              icon: <UnorderedListOutlined />,
              label: t('storage.listView'),
            },
          ]}
        />
        <Space wrap>
          <Popover
            trigger="click"
            placement="bottomRight"
            title={t('storage.workspace.displaySettings')}
            content={
              <div className="storage-display-settings">
                <label>
                  {t('storage.labelMode')}
                  <Segmented
                    block
                    value={props.labelMode}
                    onChange={props.onLabelMode}
                    options={[
                      {
                        value: 'alphanumeric',
                        label: t('storage.labelModeAlpha'),
                      },
                      {
                        value: 'numeric',
                        label: t('storage.labelModeNumeric'),
                      },
                    ]}
                  />
                </label>
                <label>
                  {t('storage.displayMode')}
                  <Select
                    aria-label={t('storage.displayMode')}
                    value={props.display}
                    onChange={props.onDisplay}
                    options={[
                      {
                        value: 'none',
                        label: t('storage.workspace.displayNone'),
                      },
                      {
                        value: 'sampleNo',
                        label: t('storage.displaySampleNo'),
                      },
                      {
                        value: 'cellLineMaterial',
                        label: t('storage.displayCellLineMaterial'),
                      },
                      { value: 'label', label: t('storage.displayLabel') },
                      { value: 'passage', label: t('storage.displayPassage') },
                    ]}
                  />
                </label>
              </div>
            }
          >
            <Button type="text" icon={<SettingOutlined />}>
              {t('storage.workspace.displaySettings')}
            </Button>
          </Popover>
          <Button
            className="storage-batch-toggle"
            onClick={() => {
              if (action) {
                setAction(null);
                props.onSelectedIds([]);
              } else start();
            }}
            disabled={!action && options.every((option) => option.disabled)}
          >
            {t(
              action ? 'storage.exitBatchSelection' : 'storage.workspace.batch',
            )}
          </Button>
        </Space>
      </div>
      {action && (
        <div className="storage-task-toolbar">
          <Space wrap>
            <Typography.Text strong>
              {t('storage.workspace.batch')}
            </Typography.Text>
            <Select
              aria-label={t('storage.workspace.task')}
              value={action}
              options={options}
              onChange={(value) => {
                setAction(value);
                props.onSelectedIds([]);
              }}
            />
            <Typography.Text type="secondary">
              {t(
                action === 'putaway'
                  ? 'storage.workspace.emptyOnly'
                  : 'storage.workspace.occupiedOnly',
              )}
            </Typography.Text>
          </Space>
        </div>
      )}
      <div className="storage-workbench-content">
        <div className="storage-workbench-map">
          <div className="storage-workbench-heading">
            <Typography.Title level={5}>
              {t('storage.workspace.positions')}
            </Typography.Title>
            <Typography.Text type="secondary">
              {t(
                action
                  ? 'storage.workspace.selectTargets'
                  : 'storage.workspace.clickDetails',
              )}
            </Typography.Text>
          </div>
          {props.view === 'grid' ? (
            <div className="storage-well-scroll">
              <div
                className="storage-well-grid"
                style={{
                  gridTemplateColumns: `20px repeat(${columns.length}, minmax(0, 1fr))`,
                }}
              >
                <span />
                {columns.map((column, index) => (
                  <span className="storage-well-axis" key={column}>
                    {index + 1}
                  </span>
                ))}
                {rows.flatMap((row, rowOffset) => [
                  <button
                    key={`row-${row}`}
                    type="button"
                    className="storage-well-axis"
                    disabled={!action}
                    aria-label={t('storage.workspace.selectRow', {
                      row: rowOffset + 1,
                    })}
                    onClick={() => toggleRow(row)}
                  >
                    {props.labelMode === 'alphanumeric'
                      ? String.fromCharCode(65 + rowOffset)
                      : rowOffset + 1}
                  </button>,
                  ...columns.map((column) => {
                    const position = props.box.positions.find(
                      (p) => p.rowIndex === row && p.columnIndex === column,
                    );
                    if (!position) return <span key={`${row}-${column}`} />;
                    const chosen = action
                      ? props.selectedIds.includes(position.id)
                      : selected?.id === position.id;
                    return (
                      <Tooltip
                        key={position.id}
                        open={chosen ? false : undefined}
                        mouseEnterDelay={0.5}
                        styles={{ root: { pointerEvents: 'none' } }}
                        title={
                          position.sample
                            ? `${props.positionLabel(position)} · ${props.positionText(position)}`
                            : t('storage.empty')
                        }
                      >
                        <button
                          type="button"
                          className={`storage-well ${position.occupied ? 'is-occupied' : ''} ${chosen ? 'is-selected' : ''}`}
                          aria-label={`${props.positionLabel(position)} · ${t(position.occupied ? 'storage.occupied' : 'storage.empty')}${position.sample ? ' · ' + props.positionText(position) : ''}`}
                          aria-pressed={chosen}
                          disabled={Boolean(action && !eligible(position))}
                          onClick={() => select(position)}
                        >
                          <span>{props.positionLabel(position)}</span>
                          {position.sample && props.display !== 'none' && (
                            <span className="storage-well-content">
                              {props.positionText(position)}
                            </span>
                          )}
                          <span
                            className="storage-well-state"
                            aria-hidden="true"
                          >
                            {position.occupied ? '●' : '—'}
                          </span>
                          {chosen && (
                            <CheckOutlined className="storage-well-check" />
                          )}
                        </button>
                      </Tooltip>
                    );
                  }),
                ])}
              </div>
            </div>
          ) : (
            <>
              <Input.Search
                allowClear
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={t('storage.listSearchPlaceholder')}
                aria-label={t('storage.listSearchPlaceholder')}
              />
              <Table<Position>
                className="storage-compact-list"
                rowKey="id"
                size="small"
                pagination={false}
                dataSource={filtered}
                scroll={{ x: 420 }}
                expandable={{
                  rowExpandable: (p) => Boolean(p.sample),
                  expandedRowRender: (p) => (
                    <Descriptions
                      size="small"
                      column={{ xs: 1, sm: 2 }}
                      items={[
                        {
                          key: 'sampleNo',
                          label: t('samples.number'),
                          children: p.sample?.sampleNo || '—',
                        },
                        {
                          key: 'material',
                          label: t('samples.material'),
                          children: p.sample?.materialName || '—',
                        },
                        {
                          key: 'passage',
                          label: t('samples.passage'),
                          children: p.sample?.passage || '—',
                        },
                        {
                          key: 'barcode',
                          label: t('samples.barcode'),
                          children: p.sample?.barcode || '—',
                        },
                        {
                          key: 'label',
                          label: t('samples.label'),
                          children:
                            props.sampleLabel(p.sample ?? undefined) || '—',
                        },
                        {
                          key: 'count',
                          label: t('samples.cellCount'),
                          children: p.sample?.cellCount || '—',
                        },
                        {
                          key: 'freeze',
                          label: t('samples.freezeDate'),
                          children: props.date(p.sample?.freezeDate),
                        },
                        {
                          key: 'expiry',
                          label: t('samples.expiryDate'),
                          children: props.date(p.sample?.expiryDate),
                        },
                        {
                          key: 'owner',
                          label: t('samples.ownerName'),
                          children:
                            props.ownerName(p.sample ?? undefined) || '—',
                        },
                        {
                          key: 'notes',
                          label: t('samples.notes'),
                          children:
                            props.sampleNotes(p.sample ?? undefined) || '—',
                        },
                      ]}
                    />
                  ),
                }}
                rowClassName={(position) =>
                  !action && position.id === selected?.id
                    ? 'storage-current-row'
                    : ''
                }
                onRow={(position) => ({
                  onClick: () => {
                    if (!action) props.onSelect(position);
                  },
                })}
                {...(action
                  ? {
                      rowSelection: {
                        selectedRowKeys: props.selectedIds,
                        onChange: (keys: React.Key[]) =>
                          props.onSelectedIds(keys.map(String)),
                        getCheckboxProps: (position: Position) => ({
                          disabled: !eligible(position),
                        }),
                      },
                    }
                  : {})}
                columns={[
                  {
                    title: t('storage.position'),
                    fixed: 'left',
                    width: 80,
                    render: (_, p) => (
                      <Button
                        type="link"
                        disabled={Boolean(action && !eligible(p))}
                        onClick={(event) => {
                          event.stopPropagation();
                          select(p);
                        }}
                      >
                        {props.positionLabel(p)}
                      </Button>
                    ),
                  },
                  {
                    title: t('storage.status'),
                    width: 90,
                    render: (_, p) =>
                      p.sample ? (
                        props.sampleStatus(p.sample.status)
                      ) : (
                        <Tag>{t('storage.empty')}</Tag>
                      ),
                  },
                  {
                    title: t('samples.cellLine'),
                    render: (_, p) => p.sample?.cellLineCode || '—',
                  },
                  {
                    title: t('samples.ownerName'),
                    render: (_, p) =>
                      props.ownerName(p.sample ?? undefined) || '—',
                  },
                ]}
              />
            </>
          )}
          <div className="storage-well-legend">
            <span>
              <i className="is-occupied" />● {t('storage.occupied')}
            </span>
            <span>
              <i />— {t('storage.empty')}
            </span>
            <span>
              <i className="is-selected" />✓ {t('storage.workspace.selected')}
            </span>
          </div>
        </div>
        <aside className="storage-workbench-detail">
          {action ? (
            <>
              <Typography.Text type="secondary">
                {options.find((option) => option.value === action)?.label}
              </Typography.Text>
              <Typography.Title level={4} aria-live="polite">
                {t('storage.workspace.selectedCount', {
                  count: selectedRows.length,
                })}
              </Typography.Title>
              <Space wrap>
                <Button
                  onClick={() =>
                    props.onSelectedIds(
                      props.box.positions.filter(eligible).map((p) => p.id),
                    )
                  }
                >
                  {t('storage.workspace.selectEligible')}
                </Button>
                <Button
                  type="text"
                  disabled={!props.selectedIds.length}
                  onClick={() => props.onSelectedIds([])}
                >
                  {t('storage.clearSelection')}
                </Button>
              </Space>
              <p className="storage-selection-labels">
                {selectedRows.map(props.positionLabel).join(' · ') ||
                  t('storage.batchHint')}
              </p>
              <Typography.Paragraph type="secondary">
                {t(`storage.workspace.next.${action}`)}
              </Typography.Paragraph>
              {action === 'putaway' && selectedRows.length > 100 && (
                <Alert
                  showIcon
                  type="warning"
                  title={t('storage.entry.maxCount')}
                />
              )}
              <Button
                block
                type="primary"
                danger={action === 'remove'}
                disabled={
                  !selectedRows.length ||
                  (action === 'putaway' && selectedRows.length > 100)
                }
                onClick={() => {
                  if (action === 'putaway') props.onCreate(selectedRows);
                  if (action === 'request') props.onRequest(selectedRows);
                  if (action === 'remove') props.onBatchRemove();
                }}
              >
                {t('storage.workspace.nextStep', {
                  count: selectedRows.length,
                })}
              </Button>
            </>
          ) : selected ? (
            <>
              <div className="storage-workbench-heading">
                <Typography.Title level={3}>
                  {props.positionLabel(selected)}
                </Typography.Title>
                {selected.sample ? (
                  props.sampleStatus(selected.sample.status)
                ) : (
                  <Tag>{t('storage.empty')}</Tag>
                )}
              </div>
              {selected.sample ? (
                <>
                  <Descriptions
                    size="small"
                    column={1}
                    items={[
                      {
                        key: 'number',
                        label: t('samples.number'),
                        children: selected.sample.sampleNo,
                      },
                      {
                        key: 'label',
                        label: t('samples.label'),
                        children: props.sampleLabel(selected.sample) || '—',
                      },
                      {
                        key: 'line',
                        label: t('samples.cellLine'),
                        children: selected.sample.cellLineCode,
                      },
                      {
                        key: 'material',
                        label: t('samples.material'),
                        children: selected.sample.materialName || '—',
                      },
                      {
                        key: 'passage',
                        label: t('samples.passage'),
                        children: selected.sample.passage || '—',
                      },
                      {
                        key: 'freeze',
                        label: t('samples.freezeDate'),
                        children: props.date(selected.sample.freezeDate),
                      },
                      {
                        key: 'barcode',
                        label: t('samples.barcode'),
                        children: selected.sample.barcode || '—',
                      },
                      {
                        key: 'owner',
                        label: t('samples.ownerName'),
                        children: props.ownerName(selected.sample) || '—',
                      },
                      {
                        key: 'count',
                        label: t('samples.cellCount'),
                        children: selected.sample.cellCount || '—',
                      },
                      {
                        key: 'expiry',
                        label: t('samples.expiryDate'),
                        children: props.date(selected.sample.expiryDate),
                      },
                      {
                        key: 'notes',
                        label: t('samples.notes'),
                        children: props.sampleNotes(selected.sample) || '—',
                      },
                    ]}
                  />
                  <div className="storage-detail-actions">
                    <Button
                      icon={<EditOutlined />}
                      disabled={!props.canEdit}
                      onClick={() => props.onEdit(selected)}
                    >
                      {t('samples.edit')}
                    </Button>
                    <Button
                      disabled={!props.canRequest}
                      onClick={() => props.onRequest([selected])}
                    >
                      {t('storage.createBatchRequest')}
                    </Button>
                    <Button
                      type="text"
                      danger
                      disabled={!props.canRemove}
                      icon={<DeleteOutlined />}
                      onClick={() => props.onRemove(selected)}
                    >
                      {t('storage.removeSample')}
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <Typography.Paragraph type="secondary">
                    {t('storage.workspace.emptyHint')}
                  </Typography.Paragraph>
                  <Button
                    block
                    type="primary"
                    disabled={!props.canCreate}
                    icon={<PlusOutlined />}
                    onClick={() => props.onCreate([selected])}
                  >
                    {t('storage.createSample')}
                  </Button>
                </>
              )}
            </>
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={t('storage.selectPosition')}
            />
          )}
        </aside>
      </div>
    </section>
  );
}
